import React, { useState, useEffect } from 'react'
import type { FixtureInsightsResponse } from '../types'
import { InsightCard } from './InsightCard'
import { PatternBadges } from './PatternBadge'
import { AnomalyBadges } from './AnomalyBadge'
import { apiGet } from '../api'

interface InsightsSummaryProps {
  fixtureId: number
  compact?: boolean
  maxInsights?: number
}

export function InsightsSummary({
  fixtureId,
  compact = false,
  maxInsights = 5,
}: InsightsSummaryProps) {
  const [data, setData] = useState<FixtureInsightsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function fetchInsights() {
      try {
        setLoading(true)
        const response = await apiGet<FixtureInsightsResponse>(
          `/insights/fixture/${fixtureId}`
        )
        if (!cancelled) {
          setData(response)
          setError(null)
        }
      } catch (err: any) {
        if (!cancelled) {
          // 404 means no insights available yet - not an error
          if (err?.status === 404) {
            setData(null)
          } else {
            setError('Failed to load insights')
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchInsights()
    return () => {
      cancelled = true
    }
  }, [fixtureId])

  if (loading) {
    return (
      <div
        style={{
          padding: '0.75rem',
          background: 'var(--card)',
          borderRadius: '8px',
          fontSize: '0.75rem',
          color: 'var(--muted)',
        }}
      >
        Loading insights...
      </div>
    )
  }

  if (error) {
    return null // Silently fail - insights are supplementary
  }

  if (!data || (data.insights.length === 0 && data.patterns.length === 0)) {
    return null // No insights to show
  }

  // Compact mode: just show pattern badges
  if (compact) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        {data.patterns.length > 0 && (
          <PatternBadges patterns={data.patterns} limit={4} size="sm" />
        )}
        {data.anomalies.length > 0 && (
          <AnomalyBadges anomalies={data.anomalies} limit={3} compact />
        )}
      </div>
    )
  }

  const visibleInsights = expanded ? data.insights : data.insights.slice(0, maxInsights)
  const hasMore = data.insights.length > maxInsights

  return (
    <div
      style={{
        background: 'var(--card)',
        borderRadius: '8px',
        padding: '0.75rem',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '0.625rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1rem' }}>AI</span>
          <span
            style={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              color: 'var(--fg)',
            }}
          >
            Match Insights
          </span>
          <span
            style={{
              fontSize: '0.625rem',
              color: 'var(--muted)',
              background: 'rgba(255,255,255,0.05)',
              padding: '0.125rem 0.375rem',
              borderRadius: '4px',
            }}
          >
            {data.insights.length} found
          </span>
        </div>
      </div>

      {/* Summary paragraph if available */}
      {data.summary && (
        <div
          style={{
            fontSize: '0.75rem',
            color: 'var(--muted)',
            lineHeight: 1.5,
            marginBottom: '0.75rem',
            paddingBottom: '0.625rem',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {data.summary}
        </div>
      )}

      {/* Pattern badges row */}
      {data.patterns.length > 0 && (
        <div style={{ marginBottom: '0.625rem' }}>
          <PatternBadges patterns={data.patterns} limit={5} size="md" />
        </div>
      )}

      {/* Anomaly badges row */}
      {data.anomalies.length > 0 && (
        <div style={{ marginBottom: '0.625rem' }}>
          <AnomalyBadges anomalies={data.anomalies} limit={4} compact={false} />
        </div>
      )}

      {/* Insight cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {visibleInsights.map((insight, i) => (
          <InsightCard key={i} insight={insight} />
        ))}
      </div>

      {/* Show more button */}
      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            width: '100%',
            marginTop: '0.5rem',
            padding: '0.5rem',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            color: 'var(--muted)',
            fontSize: '0.75rem',
            cursor: 'pointer',
          }}
        >
          {expanded ? 'Show Less' : `Show ${data.insights.length - maxInsights} More`}
        </button>
      )}

      {/* Cache info */}
      {data.cached_at && (
        <div
          style={{
            marginTop: '0.5rem',
            fontSize: '0.5625rem',
            color: 'var(--muted)',
            opacity: 0.6,
            textAlign: 'right',
          }}
        >
          Updated {new Date(data.cached_at).toLocaleTimeString()}
        </div>
      )}
    </div>
  )
}

export default InsightsSummary
